import React, { useCallback, useState } from "react";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import Cropper, { Area } from "react-easy-crop";
import "react-easy-crop/react-easy-crop.css";
import { useTranslation } from "react-i18next";
import { cropImageToBlob } from "../../helpers/cropImage";

type Props = {
  isOpen: boolean;
  imageSrc: string | null;
  /** Proporção do recorte (logo ~3:1; avatar 1). */
  aspect: number;
  /** Máscara circular no preview (avatar). */
  cropShape?: "rect" | "round";
  /** Título i18n key. */
  titleKey: string;
  fileName?: string;
  onCancel: () => void;
  onConfirm: (file: File) => void | Promise<void>;
};

/**
 * Modal de crop antes do upload (LAC-085 / LAC-072).
 * Por quê: Carla ajusta enquadramento no browser — sem editor externo.
 */
const ImageCropModal = ({
  isOpen,
  imageSrc,
  aspect,
  cropShape = "rect",
  titleKey,
  fileName = "crop.png",
  onCancel,
  onConfirm,
}: Props) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<Area | null>(null);
  const [busy, setBusy] = useState(false);

  const onCropComplete = useCallback((_: Area, pixels: Area) => {
    setArea(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !area || busy) {
      return;
    }
    setBusy(true);
    try {
      const blob = await cropImageToBlob(imageSrc, area);
      const file = new File([blob], fileName, { type: blob.type || "image/png" });
      await onConfirm(file);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={onCancel} centered size="lg" backdrop="static">
      <ModalHeader toggle={busy ? undefined : onCancel}>
        {t(titleKey)}
      </ModalHeader>
      <ModalBody>
        <div
          className="position-relative bg-dark rounded overflow-hidden"
          style={{ height: 320 }}
        >
          {imageSrc ? (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={aspect}
              cropShape={cropShape}
              showGrid={cropShape === "rect"}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
            />
          ) : null}
        </div>
        <div className="mt-3">
          <label className="form-label small mb-1" htmlFor="crop-zoom">
            {t("settings.account.crop.zoom")}
          </label>
          <input
            id="crop-zoom"
            type="range"
            className="form-range"
            min={1}
            max={3}
            step={0.05}
            value={zoom}
            disabled={busy}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
        <p className="text-muted small mb-0 mt-2">
          {t("settings.account.crop.hint")}
        </p>
      </ModalBody>
      <ModalFooter>
        <Button color="light" type="button" disabled={busy} onClick={onCancel}>
          {t("settings.account.crop.cancel")}
        </Button>
        <Button color="primary" type="button" disabled={busy || !area} onClick={() => void handleConfirm()}>
          {busy ? <Spinner size="sm" className="me-1" /> : null}
          {t("settings.account.crop.apply")}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ImageCropModal;
